// Configuración dinámica de la URL base de la API
const __isDev = /localhost|127\.0\.0\.1/.test(window.location.hostname) || window.DEBUG === true;
const debugLog = (...args) => { if (__isDev) try { console.log.apply(console, args); } catch(_) {} };
export const getApiBaseUrl = () => {
    const { hostname: host, protocol, port } = window.location;
    // URL base para todas las rutas de la API
    // Regla: si estamos en desarrollo (localhost/127.0.0.1 o puertos típicos de FE), apuntar a :3000
    const isLocalHost = /localhost|127\.0\.0\.1/.test(host);
    const isDevPort = ['3001', '5173', '8080', '5174'].includes(port);
    const computed = (isLocalHost || isDevPort)
        ? `${protocol}//${host}:3000/api/v1`
        : `${protocol}//${host}/api/v1`;
    return computed;
};

// Configuración global de la API
const apiVersion = 'v1';
let baseApiUrl = getApiBaseUrl();
// Guardia: si por alguna razón alguien inyectó una base de Supabase en producción, forzamos el mismo dominio
try {
    if (!/localhost|127\.0\.0\.1/.test(window.location.hostname) && /supabase\.(co|in)/i.test(String(baseApiUrl))) {
        baseApiUrl = `${window.location.protocol}//${window.location.hostname}/api/v1`;
    }
} catch {}

// TODAS las rutas usan /api/v1/ como prefijo
export const apiConfig = {
    baseURL: baseApiUrl,
    adminURL: baseApiUrl,
    authURL: baseApiUrl
};
// Helper para WebSocket (messaging, etc.)
export function getWsBaseUrl() {
    try {
        const { protocol, hostname } = window.location;
        const isLocal = /localhost|127\.0\.0\.1/.test(hostname);
        const wsProto = protocol === 'https:' ? 'wss:' : 'ws:';
        return isLocal ? `${wsProto}//${hostname}:3000` : `${wsProto}//${hostname}`;
    } catch {
        // Fallback conservador
        return 'wss://www.publikaya.com';
    }
}
// Exponer globalmente y congelar para evitar sobrescrituras accidentales
try {
    if (typeof window !== 'undefined') {
        window.apiConfig = Object.freeze({ ...apiConfig });
        // Exponer helper WS
        window.getWsBaseUrl = getWsBaseUrl;
    }
} catch {}

// Función para construir URLs de la API
export function apiUrl(path) {
    // Todas las rutas usan la misma URL base con /api/v1
    const basePath = apiConfig.baseURL;
    // Asegurarse de que no haya dobles barras
    return `${basePath.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

// Función para construir URLs de imágenes (uploads)
export function getImageUrl(imagePath) {
    debugLog('🔍 getImageUrl recibió:', imagePath);
    if (!imagePath) return 'https://cdn.publikaya.com/assets/images/placeholder-business.webp';
    const loc = window.location;
    const isLocal = /localhost|127\.0\.0\.1/.test(loc.hostname);
    const backendOrigin = isLocal ? `${loc.protocol}//${loc.hostname}:3000` : `${loc.origin}`;

    // Si ya es URL absoluta, normalizar si apunta a /uploads en otro host (p.ej. 3001)
    if (/^https?:\/\//i.test(imagePath)) {
        try {
            const u = new URL(imagePath);
            // Normalizar URLs de Supabase que tienen 'public/public' duplicado
            if (/supabase\.(co|in)/i.test(u.hostname)) {
                const fixedPath = u.pathname.replace('/storage/v1/object/public/public/', '/storage/v1/object/public/');
                if (fixedPath !== u.pathname) {
                    return `${u.origin}${fixedPath}${u.search}`;
                }
            }
            // Si es una URL de R2 de vitrinaplus, usar worker con caché
            if (u.hostname.includes('r2.dev') || u.hostname.includes('r2.cloudflarestorage.com')) {
                const cachedUrl = imagePath.replace(/https:\/\/[^/]+\.r2\.(dev|cloudflarestorage\.com)/, 'https://r2-cache.miotroplaneta2008.workers.dev');
                debugLog('🚀 URL de R2 detectada, reescribiendo a worker con caché:', cachedUrl);
                return cachedUrl;
            }
            if (u.pathname.startsWith('/uploads/')) {
                const localUrl = `${backendOrigin}${u.pathname}`;
                debugLog('🏠 Convirtiendo URL absoluta a local:', localUrl);
                return localUrl;
            }
        } catch {}
        return imagePath;
    }

    // Normalizar relativos
    let path = imagePath.trim();
    // Si viene con uploads sin slash inicial
    if (path.startsWith('uploads/')) path = `/${path}`;
    // Si ya viene con /uploads/... verificar si es vitrinaplus para usar R2 público
    if (path.startsWith('/uploads/')) {
        // Si es una imagen de vitrinaplus o productos, usar la URL pública de R2
        if (path.includes('/vitrinaplus/') || path.includes('/productos/')) {
            const r2Url = `https://r2-cache.miotroplaneta2008.workers.dev${path}`;
            const tipo = path.includes('/vitrinaplus/') ? 'vitrinaplus' : 'productos';
            debugLog(`🚀 Devolviendo URL de R2 para ${tipo}:`, r2Url);
            return r2Url;
        }
        // Para otras rutas de uploads, usar backend origin
        const localUrl = `${backendOrigin}${path}`;
        debugLog('🏠 Devolviendo URL local:', localUrl);
        return localUrl;
    }
    // Si viene con un path relativo con subcarpetas, únelo directo
    if (path.includes('/')) {
        return `${backendOrigin}/${path.replace(/^\/+/, '')}`;
    }
    // En caso de solo nombre de archivo, decidir carpeta por extensión/forma
    const lower = path.toLowerCase();
    const isProductExt = lower.endsWith('.webp') || lower.endsWith('.jpg') || lower.endsWith('.jpeg');
    const isPng = lower.endsWith('.png');
    // Heurística: imágenes .webp/.jpg son de productos; .png suelen ser banners/logos fijos
    if (isProductExt) {
        path = `/uploads/products/${path}`;
    } else if (isPng) {
        // Por defecto tratar .png sueltos como banners
        path = `/uploads/banners/${path}`;
    } else {
        // Desconocido: placeholder
        return 'https://cdn.publikaya.com/assets/images/placeholder-business.webp';
    }
    return `${backendOrigin}${path}`;
}

// Función específica para banners de negocios
export function getBannerUrl(businessId, extension = 'png') {
    return getImageUrl(`/uploads/banners/${businessId}.${extension}`);
}

// Función específica para logos de negocios  
export function getLogoUrl(businessId, extension = 'png') {
    return getImageUrl(`/uploads/logos/${businessId}.${extension}`);
}

debugLog(`✅ API Config cargada correctamente`, window.apiConfig);

// Exportar si usas módulos
// export default API_URL;
